import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Heart, MessageCircle, Star, Loader2, Send, Eye, EyeOff, Trash2, Flag, CheckCircle2 } from "lucide-react";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { FeedbackVideoPlayer } from "./FeedbackVideoPlayer";
import { PostAttachments } from "./PostAttachment";
import { ConfirmDialog } from "./ConfirmDialog";
import { ProfileAvatar } from "../build/ProfileAvatar";
import { MemberLink } from "../build/MemberLink";
import { CollaborationCard } from "./CollaborationCard";
import { CreativeRadarCard } from "./CreativeRadarCard";

export function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Nav Cleanup, Stage 2B (2026-11): "Editor, Director and Cinematographer" -> "Editors,
// Directors and Cinematographers" — naive plural (+s) is enough for this app's role
// taxonomy (no irregular plurals in the Role Directory). Shared by the OPEN card's audience
// line and the not-eligible comment-box message below, so both phrase the exact same way.
function joinRolesNaturally(roles) {
  const plural = (roles || []).map((r) => `${r}s`);
  if (plural.length === 0) return "";
  if (plural.length === 1) return plural[0];
  if (plural.length === 2) return `${plural[0]} and ${plural[1]}`;
  return `${plural.slice(0, -1).join(", ")} and ${plural[plural.length - 1]}`;
}

// Creator of the Week week range (2026-11 design pass): "the edition's period if available,
// else pinned_at to pinned_at + 6 days" — Creator of the Week is never tied to a weekly-run
// edition (selected independently, see modules/creative_radar.py), so this is always the
// pinned_at window; kept as a standalone helper for that one "if available" case to slot into
// later without touching the render logic.
export function formatWeekRange(pinnedAtIso) {
  if (!pinnedAtIso) return "";
  const start = new Date(pinnedAtIso);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
  const month = (d) => d.toLocaleDateString("en-US", { month: "short" });
  if (month(start) === month(end)) return `${start.getDate()} – ${end.getDate()} ${month(end)}`;
  return `${start.getDate()} ${month(start)} – ${end.getDate()} ${month(end)}`;
}

// @-mentions (2026-08): renders "@Full Name" substrings that match a REAL member in the
// already-loaded `members` list (the same active-members list the Floor feed always fetches —
// no new data source) as a `MemberLink` to their public profile. Longest-name-first, earliest-
// occurrence, CASE-INSENSITIVE matching (2026-08 fix: "@shayan shukla" now resolves the same
// as "@Shayan Shukla") so "@Ana" never accidentally swallows part of "@Ana Maria Rodrigues".
// Any "@word" that doesn't match a real member's exact name renders as plain text — never a
// dead/guessed link.
function renderMentions(text, members) {
  if (!text || !members?.length) return text;
  const sorted = [...members].filter((m) => m.name).sort((a, b) => b.name.length - a.name.length);
  const nodes = [];
  let remaining = text;
  let key = 0;
  while (remaining.length) {
    let best = null;
    const remainingLower = remaining.toLowerCase();
    for (const m of sorted) {
      const idx = remainingLower.indexOf(`@${m.name.toLowerCase()}`);
      if (idx !== -1 && (!best || idx < best.idx)) best = { idx, member: m };
    }
    if (!best) { nodes.push(remaining); break; }
    if (best.idx > 0) nodes.push(remaining.slice(0, best.idx));
    const matchedText = remaining.slice(best.idx, best.idx + best.member.name.length + 1);
    nodes.push(
      <MemberLink key={`mention-${key++}`} freelancerId={best.member.id} className="font-semibold text-brand-dim hover:underline" testId={`community-mention-link-${best.member.id}-${key}`}>
        {matchedText}
      </MemberLink>
    );
    remaining = remaining.slice(best.idx + best.member.name.length + 1);
  }
  return nodes;
}

// Phase 4 (2026-02): `isModerator` (community.moderate holder) unlocks pin/feature/hide + any
// post/comment's delete, inline on every card — reactive moderation replaces the old
// approval-before-publish gate. Every viewer (moderator or not) gets the "Report" action.
//
// Founder go-live hardening (2026-02): a freelancer can now ALSO delete their OWN post/comment
// (`post.author_id === viewerId`/`c.author_id === viewerId` — ownership enforced server-side
// too, see modules/community.py::_can_delete; this UI check is purely which button to SHOW,
// never the actual gate). Every delete (self or moderator) now goes through `ConfirmDialog` —
// no hard delete anywhere on this card fires without an explicit confirm.
//
// Design pass (2026-02): "The Floor" feed's signature contrast — an NJM announcement
// (`post_type==="announce"`) renders as a dark, ink card (matching the reference mockup's
// black announcement cards); every ordinary freelancer work post stays a light paper card.
// Purely a `dark`-boolean class swap below — no behavior, data, or permission changes.
export function PostCard({ post, members, isModerator, viewerId, viewerIsFreelancer, onChanged, autoOpenComments, onAutoOpened, isVideoPlaying, onPlayVideo }) {
  const dark = post.post_type === "announce" || post.post_type === "creator_of_week";
  const isCotw = post.post_type === "creator_of_week";
  const isAroundNetwork = post.post_type === "around_network";
  const cotw = post.cotw_entry || {};
  const cotwFirstName = (cotw.name || "this creator").split(" ")[0];
  const canDeletePost = isModerator || post.can_delete;
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [commentCount, setCommentCount] = useState(post.comment_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(false);
  const [reportedComments, setReportedComments] = useState(() => new Set());
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(null);
  const commentInputRef = useRef(null);

  // Notification deep-link (2026-08): a mention/collaboration/announcement notification
  // pointing at this exact post auto-expands its comments, same "land on the actual item"
  // behavior My Work's assignment deep-links already have.
  // iteration_187 fix: `loadComments` must be a HOISTED function declaration (it is defined
  // below, after the collaboration/radar early returns, which themselves must stay after every
  // hook) — as a `const` arrow it threw "Cannot access 'loadComments' before initialization"
  // and crashed The Floor whenever a postId deep-link landed. Also forces the thread OPEN
  // (`true`) instead of toggling, so React's dev StrictMode double-effect can't close it again.
  useEffect(() => {
    if (autoOpenComments && post.post_type !== "radar" && post.post_type !== "collaboration") { loadComments(true); }
    if (autoOpenComments) onAutoOpened?.();
  }, [autoOpenComments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collaboration Opportunities Phase 1 (2026-08): a fully distinct card (own component, own
  // layout) — never squeezed into the work/announce text+attachments layout below. Placed
  // AFTER every hook above so hook call order never changes across renders/post types.
  if (post.post_type === "collaboration") {
    return <CollaborationCard post={post} isModerator={isModerator} viewerId={viewerId} viewerIsFreelancer={viewerIsFreelancer} onChanged={onChanged} />;
  }
  if (post.post_type === "radar") {
    return <CreativeRadarCard post={post} />;
  }

  const toggleLike = async () => {
    try {
      if (liked) { await api.delete(`/v1/community/posts/${post.id}/like`); setLiked(false); setLikeCount((c) => c - 1); }
      else { await api.post(`/v1/community/posts/${post.id}/like`); setLiked(true); setLikeCount((c) => c + 1); }
    } catch { toast.error("Couldn't update like"); }
  };

  async function loadComments(forceOpen) {
    setShowComments((s) => (forceOpen ? true : !s));
    if (!comments) {
      const r = await api.get(`/v1/community/posts/${post.id}/comments`);
      setComments(r.data.items);
    }
  }

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/v1/community/posts/${post.id}/comments`, { text: commentText });
      setComments((c) => [...(c || []), r.data.comment]);
      setCommentCount((c) => c + 1);
      setCommentText("");
      setMentionOpen(false);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "feedback_closed") toast.error("This feedback request is closed");
      else if (code === "feedback_role_restricted") toast.error("Feedback on this request is limited to specific roles");
      else toast.error("Couldn't post comment");
    }
    finally { setPosting(false); }
  };

  // @-mentions (2026-08): the comment input tracks the token right after the most recent "@"
  // (only if it directly follows whitespace/start-of-text, so an email-like "user@site" never
  // triggers it) and offers a live-filtered dropdown from the SAME active-members list already
  // loaded for this feed (`members` prop) — never a new data source. Query stays a single,
  // space-free token; the inserted mention is always the member's FULL real name.
  const mentionMatches = mentionOpen
    ? (members || [])
        .filter((m) => m.name && m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        .map((m, i) => {
          const nameLower = m.name.toLowerCase();
          const q = mentionQuery.toLowerCase();
          // Rank (2026-08 fix): prefix match on the full name beats a prefix match on any
          // later word, which beats a plain substring anywhere — so typing "@Shay" always
          // ranks "Shayan Shukla" above an unrelated "Akshay Pai" (which only matches
          // mid-word). Original list order is the tiebreaker within the same rank.
          let rank = 2;
          if (nameLower.startsWith(q)) rank = 0;
          else if (nameLower.split(/\s+/).some((w) => w.startsWith(q))) rank = 1;
          return { m, rank, i };
        })
        .sort((a, b) => a.rank - b.rank || a.i - b.i)
        .slice(0, 6)
        .map((x) => x.m)
    : [];

  const handleCommentChange = (e) => {
    const val = e.target.value;
    const cursor = e.target.selectionStart;
    setCommentText(val);
    const upToCursor = val.slice(0, cursor);
    const at = upToCursor.lastIndexOf("@");
    if (at !== -1 && (at === 0 || /\s/.test(upToCursor[at - 1])) && !/\s/.test(upToCursor.slice(at + 1))) {
      setMentionStart(at);
      setMentionQuery(upToCursor.slice(at + 1));
      setMentionOpen(true);
      setMentionIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const selectMention = (member) => {
    const before = commentText.slice(0, mentionStart);
    const after = commentText.slice(mentionStart + 1 + mentionQuery.length);
    setCommentText(`${before}@${member.name} ${after}`);
    setMentionOpen(false);
    requestAnimationFrame(() => commentInputRef.current?.focus());
  };

  const handleCommentKeyDown = (e) => {
    if (!mentionOpen || mentionMatches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionMatches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); }
    else if (e.key === "Enter") { e.preventDefault(); selectMention(mentionMatches[mentionIndex]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  const removeComment = async (commentId) => {
    try {
      await api.delete(`/v1/community/comments/${commentId}`);
      setComments((c) => (c || []).filter((x) => x.id !== commentId));
      setCommentCount((c) => Math.max(0, c - 1));
    } catch { toast.error("Couldn't remove comment"); }
  };

  const reportComment = async (commentId) => {
    try {
      await api.post(`/v1/community/comments/${commentId}/report`);
      setReportedComments((prev) => new Set(prev).add(commentId));
      toast.success("Reported to NJM");
    } catch { toast.error("Couldn't report this comment"); }
  };

  const report = async () => {
    try { await api.post(`/v1/community/posts/${post.id}/report`); setReported(true); toast.success("Reported to NJM"); }
    catch { toast.error("Couldn't report this post"); }
  };

  const moderate = async (action, payload) => {
    setBusy(true);
    try {
      if (action === "delete") await api.delete(`/v1/community/posts/${post.id}`);
      else await api.post(`/v1/community/posts/${post.id}/${action}`, payload);
      onChanged?.();
    } catch { toast.error("Couldn't update this post"); }
    finally { setBusy(false); }
  };

  const textCls = dark ? "text-paper" : "text-ink";
  const softCls = dark ? "text-paper/55" : "text-faint";
  const borderCls = dark ? "border-white/10" : "border-hairline";
  const actionCls = dark ? "text-paper/60 hover:text-paper" : "text-faint hover:text-ink";

  // Creator of the Week — design conformance pass (2026-11): a fully bespoke, pixel-matched
  // card (dark #141414 panel, yellow #FFC817 accents) per the approved design file, section
  // 6a/6b/6e/6f. Rendered as its own return (after every hook above, same pattern as the
  // collaboration/radar early returns) rather than squeezed into the generic work/announce
  // layout below — but reuses the SAME like/comment/report/moderate/delete handlers and the
  // SAME comment-thread markup, so none of that logic is duplicated or forked.
  if (isCotw) {
    const weekRange = formatWeekRange(post.pinned_at);
    const initials = (cotw.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
    const hasGrid = Boolean(cotw.fun_question || cotw.fun_answer || cotw.intro_copy);
    return (
      <div data-testid={`community-post-${post.id}`}
        className="rounded-[14px] bg-[#141414] border-t-[3px] border-t-[#FFC817] px-[26px] pt-[22px] pb-[10px] max-sm:px-4 max-sm:pt-[18px] max-sm:pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid={`cotw-header-${post.id}`}>
          <span className="text-[10px] font-bold tracking-[.22em] text-[#FFC817]">CREATOR OF THE WEEK</span>
          <div className="flex items-center gap-2.5">
            {weekRange && <span className="text-xs font-medium text-white/50" data-testid={`cotw-week-range-${post.id}`}>{weekRange}</span>}
            {post.pinned && (
              <span data-testid={`cotw-pinned-pill-${post.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/[.22] px-2.5 py-1 text-[9.5px] font-semibold tracking-[.1em] text-white/75">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FFC817]" /> PINNED
              </span>
            )}
          </div>
        </div>

        <div className="flex items-start gap-4 mt-4" data-testid={`cotw-identity-${post.id}`}>
          <ProfileAvatar
            freelancerId={cotw.freelancer_id}
            hasPhoto
            alt=""
            className="h-[104px] w-[104px] rounded-full object-cover shrink-0 border-[3px] border-[#FFC817] p-[3px] box-border max-sm:h-[72px] max-sm:w-[72px]"
            testId={`community-post-avatar-${post.id}`}
            fallback={
              <div data-testid={`cotw-avatar-fallback-${post.id}`}
                className="h-[88px] w-[88px] rounded-full border-[3px] border-[#FFC817] bg-[#1E1E1E] flex items-center justify-center shrink-0 font-extrabold text-[27px] text-[#FFC817] max-sm:h-[72px] max-sm:w-[72px] max-sm:text-[22px]">
                {initials}
              </div>
            }
          />
          <div className="min-w-0 flex-1 pt-1">
            <div className="font-extrabold text-[28px] leading-[1.05] text-white uppercase max-sm:text-[22px]">
              <MemberLink freelancerId={cotw.freelancer_id} className="hover:underline" testId={`community-post-author-link-${post.id}`}>
                {cotw.name}
              </MemberLink>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap mt-2.5">
              {cotw.primary_role && (
                <span data-testid={`cotw-role-chip-${post.id}`}
                  className="inline-flex border-[1.5px] border-[#FFC817] rounded-full px-[13px] py-[6px] text-[11px] font-semibold tracking-[.08em] text-[#FFC817] max-sm:px-[11px] max-sm:py-[5px] max-sm:text-[10px]">
                  {cotw.primary_role}
                </span>
              )}
              {cotw.city && (
                <span data-testid={`cotw-location-${post.id}`} className="text-[13px] font-medium text-white/72 max-sm:text-[12.5px]">
                  ◎ {cotw.city}
                </span>
              )}
            </div>
          </div>
        </div>

        {cotw.intro_text && (
          <p data-testid={`cotw-quote-${post.id}`} className="font-semibold text-[17px] leading-[1.55] text-white mt-[18px] max-sm:text-[15px] max-sm:mt-3">
            &ldquo;{cotw.intro_text}&rdquo;
          </p>
        )}

        {hasGrid && (
          <>
            <div className="h-px bg-white/[.12] mt-[18px] max-sm:mt-3.5" />
            <div data-testid={`cotw-grid-${post.id}`} className="grid grid-cols-2 gap-7 mt-[18px] max-sm:grid-cols-1 max-sm:gap-3.5 max-sm:mt-3.5">
              {(cotw.fun_question || cotw.fun_answer) && (
                <div data-testid={`cotw-we-asked-${post.id}`}>
                  <div className="text-[10px] font-bold tracking-[.22em] text-[#FFC817]">WE ASKED {cotwFirstName.toUpperCase()}</div>
                  {cotw.fun_question && <div className="mt-2 text-[14.5px] font-bold leading-[1.45] text-white">{cotw.fun_question}</div>}
                  {cotw.fun_answer && <div className="mt-1.5 text-sm leading-[1.6] text-white/82">{cotw.fun_answer}</div>}
                </div>
              )}
              {cotw.intro_copy && (
                <div data-testid={`cotw-from-njm-${post.id}`} className="max-sm:pt-3.5 max-sm:border-t max-sm:border-white/[.12]">
                  <div className="text-[10px] font-bold tracking-[.22em] text-[#FFC817]">FROM NJM</div>
                  <div className="mt-2 text-sm leading-[1.6] text-white/82">{cotw.intro_copy}</div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="mt-4 pt-4 border-t border-white/[.12]">
          <div className="flex items-center gap-2 max-sm:flex-col max-sm:items-stretch">
            <div className="order-2 flex items-center gap-1 max-sm:w-full max-sm:mt-2">
              <button onClick={toggleLike} data-testid={`community-like-btn-${post.id}`}
                className={`inline-flex items-center gap-1.5 h-11 px-2 text-sm transition-colors ${liked ? "text-[#E5484D]" : "text-white/60 hover:text-white"}`}>
                <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} /> {likeCount}
              </button>
              <button onClick={() => loadComments()} data-testid={`community-comment-toggle-${post.id}`}
                className="inline-flex items-center gap-1.5 h-11 px-2 text-sm text-white/60 hover:text-white transition-colors">
                <MessageCircle className="h-4 w-4" /> {commentCount}
              </button>
              <button onClick={report} disabled={reported} data-testid={`community-report-btn-${post.id}`}
                className="inline-flex items-center gap-1.5 h-11 px-2 text-sm text-white/60 hover:text-white disabled:opacity-40 transition-colors">
                <Flag className="h-4 w-4" /> {reported ? "Reported" : "Report"}
              </button>
            </div>
            <div className="order-1 ml-auto flex items-center gap-2.5 max-sm:ml-0 max-sm:w-full">
              <MemberLink freelancerId={cotw.freelancer_id} testId={`cotw-view-profile-${post.id}`}
                className="inline-flex items-center justify-center h-11 px-[18px] rounded-lg bg-[#FFC817] text-[#111111] text-[13.5px] font-bold hover:brightness-95 transition max-sm:flex-1">
                View profile
              </MemberLink>
              <MemberLink freelancerId={cotw.freelancer_id} testId={`cotw-view-work-${post.id}`}
                className="inline-flex items-center justify-center h-11 px-[18px] rounded-lg border-[1.5px] border-white/35 text-white text-[13.5px] font-semibold hover:border-white/55 transition max-sm:flex-1">
                View work
              </MemberLink>
            </div>
          </div>
          {isModerator && (
            <div className="flex items-center gap-3 flex-wrap mt-3">
              <button onClick={() => moderate("pin", { pinned: !post.pinned })} disabled={busy} data-testid={`community-pin-btn-${post.id}`} className="text-[11px] text-white/60 hover:text-white transition-colors">
                {post.pinned ? "Unpin" : "Pin"}
              </button>
              <button onClick={() => moderate("feature", { featured: !post.featured })} disabled={busy} data-testid={`community-feature-btn-${post.id}`} className="text-[11px] text-white/60 hover:text-white transition-colors">
                {post.featured ? "Unfeature" : "Feature"}
              </button>
              <button onClick={() => moderate("hide", { hidden: !post.hidden })} disabled={busy} data-testid={`community-hide-btn-${post.id}`} className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white transition-colors">
                {post.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {post.hidden ? "Unhide" : "Hide"}
              </button>
            </div>
          )}
          {canDeletePost && (
            <div className="mt-3">
              <ConfirmDialog
                testIdPrefix={`community-delete-post-${post.id}`}
                title="Delete this post?"
                description="This permanently removes the post, its comments, likes, and any attached images. This can't be undone."
                confirmLabel="Delete post"
                onConfirm={() => moderate("delete")}
                trigger={
                  <button disabled={busy} data-testid={`community-delete-btn-${post.id}`} className="inline-flex items-center gap-1 text-[11px] text-red-400 hover:text-red-500 transition-colors">
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                }
              />
            </div>
          )}
        </div>

        {showComments && (
          <div className="mt-3.5 space-y-2" data-testid={`community-comments-${post.id}`}>
            {(comments || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 text-xs text-paper/70">
                <span>
                  <MemberLink freelancerId={c.author_freelancer_id} className="font-medium hover:underline text-paper" testId={`community-comment-author-link-${c.id}`}>
                    {c.author_name}
                  </MemberLink>
                  : {renderMentions(c.text, members)}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {!c.is_mine && (
                    <button onClick={() => reportComment(c.id)} disabled={reportedComments.has(c.id)} data-testid={`community-comment-report-${c.id}`} className="hover:text-red-400 disabled:opacity-40 text-paper/55">
                      <Flag className="h-3 w-3" />
                    </button>
                  )}
                  {c.can_delete && (
                    <ConfirmDialog
                      testIdPrefix={`community-comment-remove-${c.id}`}
                      title="Delete this comment?"
                      description="This permanently removes the comment. This can't be undone."
                      confirmLabel="Delete comment"
                      onConfirm={() => removeComment(c.id)}
                      trigger={<button data-testid={`community-comment-remove-${c.id}`} className="shrink-0 hover:text-red-400 text-paper/55"><Trash2 className="h-3 w-3" /></button>}
                    />
                  )}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  ref={commentInputRef}
                  value={commentText} onChange={handleCommentChange} onKeyDown={handleCommentKeyDown}
                  onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
                  placeholder="Write a comment… (@ to mention someone)"
                  data-testid={`community-comment-input-${post.id}`}
                  className="w-full rounded-md px-2.5 py-1.5 text-xs placeholder:text-faint focus:outline-none focus:border-brand/50 border border-white/15 bg-white/5 text-paper"
                />
                {mentionOpen && mentionMatches.length > 0 && (
                  <div data-testid={`community-mention-menu-${post.id}`} className="absolute bottom-full left-0 mb-1 w-56 max-h-48 overflow-y-auto rounded-md border shadow-lg z-10 bg-ink border-white/15">
                    {mentionMatches.map((m, i) => (
                      <button key={m.id} type="button" onMouseDown={(e) => { e.preventDefault(); selectMention(m); }}
                        data-testid={`community-mention-option-${post.id}-${m.id}`}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors ${i === mentionIndex ? "bg-white/10" : ""} text-paper hover:bg-white/10`}>
                        <span className="font-medium">{m.name}</span>
                        {m.primary_role && <span className="text-[10px] text-paper/55">{m.primary_role}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={submitComment} disabled={posting} data-testid={`community-comment-submit-${post.id}`} className="text-brand disabled:opacity-50">
                {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`community-post-${post.id}`}
      className={`rounded-xl border p-5 ${dark ? "bg-ink border-white/5" : post.hidden ? "border-red-300 bg-red-50/40" : "bg-paper border-hairline"}`}>
      {isAroundNetwork ? (
        <div data-testid={`around-network-post-${post.id}`}>
          <span className="inline-flex items-center rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink">
            Around the Network
          </span>
          <p className="text-sm mt-2.5 text-ink" data-testid="community-post-text">{post.text}</p>
          <div className="mt-3.5 space-y-3" data-testid={`around-network-entries-${post.id}`}>
            {(post.around_network_entries || []).map((entry, i) => {
              const initials = (entry.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
              return (
                <div key={entry.freelancer_id || i} className="flex items-start gap-3" data-testid={`around-network-entry-${post.id}-${i}`}>
                  <ProfileAvatar
                    freelancerId={entry.freelancer_id}
                    hasPhoto={Boolean(entry.has_photo)}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover shrink-0"
                    testId={`around-network-entry-avatar-${post.id}-${i}`}
                    fallback={<div data-testid={`around-network-entry-avatar-${post.id}-${i}`} className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 bg-brand/15 text-ink">{initials}</div>}
                  />
                  <div className="min-w-0">
                    <MemberLink freelancerId={entry.freelancer_id} className="text-sm font-medium text-ink hover:underline" testId={`around-network-entry-name-${post.id}-${i}`}>
                      {entry.name}
                    </MemberLink>
                    <div className="text-[11px] text-faint">{[entry.primary_role, entry.city].filter(Boolean).join(" · ")}</div>
                    <p className="text-sm text-ink mt-0.5 whitespace-pre-wrap leading-snug line-clamp-2">{entry.update_text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
      <div className="flex items-center gap-3">
        {post.author_photo ? (
          <ProfileAvatar
            freelancerId={post.author_freelancer_id}
            hasPhoto={Boolean(post.author_photo)}
            alt=""
            className={`h-10 w-10 rounded-full object-cover shrink-0 ${dark ? "ring-2 ring-brand" : ""}`}
            testId={`community-post-avatar-${post.id}`}
            fallback={<div className={`h-10 w-10 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${dark ? "bg-brand text-ink" : "bg-brand/15 text-ink"}`}>{(post.author_name || "?")[0]}</div>}
          />
        ) : (
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${dark ? "bg-brand text-ink" : "bg-brand/15 text-ink"}`}>{(post.author_name || "?")[0]}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-medium truncate flex items-center gap-1.5 ${textCls}`}>
            <MemberLink freelancerId={post.author_freelancer_id} className="hover:underline" testId={`community-post-author-link-${post.id}`}>
              {post.author_name}
            </MemberLink>
            {dark && (
              <span data-testid={`community-njm-badge-${post.id}`}
                className="inline-flex items-center rounded-full bg-brand px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink shrink-0">
                NJM
              </span>
            )}
          </div>
          <div className={`text-[11px] ${softCls}`}>
            {/* Cursor re-review PR #22, item 5b: a staff post never has `author_primary_role`
                at all (community.py::_author_fields only sets it for a freelancer author) —
                "NJM Team" must only fall back for THAT case. A freelancer author who simply
                has no primary_role set on their profile DOES have the key (value null/empty)
                and must render unlabeled instead of being mislabeled as staff. */}
            {post.author_primary_role || post.author_role || (Object.prototype.hasOwnProperty.call(post, "author_primary_role") ? null : "NJM Team")}{post.author_city ? ` · ${post.author_city}` : ""} · {timeAgo(post.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {post.post_type === "feedback_request" && (
            post.feedback_status === "CLOSED" ? (
              <span data-testid={`community-feedback-status-chip-${post.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-hairline px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-soft">
                <span className="h-1.5 w-1.5 rounded-full bg-ink-soft/70" /> Feedback Closed
              </span>
            ) : (
              <span data-testid={`community-feedback-status-chip-${post.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-dim">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Open
              </span>
            )
          )}
          {post.pinned && (
            <span data-testid={`community-pinned-badge-${post.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${dark ? "border-brand/40 text-brand" : "border-hairline text-ink-soft"}`}>
              <CheckCircle2 className="h-2.5 w-2.5" /> Pinned
            </span>
          )}
          {post.featured && (
            <span data-testid={`community-featured-badge-${post.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink">
              <Star className="h-2.5 w-2.5" /> Featured
            </span>
          )}
        </div>
      </div>
      {post.post_type === "feedback_request" ? (
        <div data-testid={`community-feedback-request-${post.id}`}>
          <div className="text-[10px] font-bold tracking-[.18em] text-brand-dim uppercase mt-3.5">Feedback Request</div>
          {post.title && <h3 className="text-base font-semibold text-ink mt-1" data-testid={`community-feedback-title-${post.id}`}>{post.title}</h3>}
          {post.text && <p className="text-sm mt-1.5 text-ink whitespace-pre-wrap leading-relaxed" data-testid="community-post-text">{renderMentions(post.text, members)}</p>}
          <FeedbackVideoPlayer videoId={post.youtube_video_id} isPlaying={isVideoPlaying} onPlay={onPlayVideo} testId={`community-feedback-video-${post.id}`} />
          <div className="mt-3 rounded-lg bg-surface2/60 border border-hairline px-3.5 py-2.5">
            <div className="text-[10px] font-bold tracking-wider uppercase text-ink-soft">Feedback wanted on</div>
            <p className="text-sm mt-1 text-ink leading-relaxed" data-testid={`community-feedback-prompt-${post.id}`}>{post.feedback_prompt}</p>
          </div>
          <div className="text-xs text-faint mt-2.5" data-testid={`community-feedback-audience-${post.id}`}>
            {post.feedback_scope === "SELECTED_ROLES" && post.allowed_roles?.length > 0
              ? <>Open to <span className="font-medium text-ink-soft">{joinRolesNaturally(post.allowed_roles)}</span></>
              : "Open to everyone"}
          </div>
        </div>
      ) : (
        <>
          <p className={`text-sm mt-3.5 whitespace-pre-wrap leading-relaxed ${dark ? "text-paper/90" : "text-ink"}`} data-testid="community-post-text">{renderMentions(post.text, members)}</p>
          {post.link_url && <LinkPreviewCard url={post.link_url} />}
          <PostAttachments postId={post.id} attachments={post.attachments} />
          {post.tagged_contributor_ids?.length > 0 && (
            <div className={`text-[11px] mt-2.5 ${softCls}`} data-testid={`community-post-tagged-${post.id}`}>
              <span className="font-medium uppercase tracking-wider text-[10px] mr-1.5">With</span>
              {post.tagged_contributor_ids.map((id, i) => {
                const m = (members || []).find((x) => x.id === id);
                if (!m) return null;
                return (
                  <span key={id}>
                    <span className={`font-medium ${textCls}`}>{m.name}</span>
                    {m.primary_role ? ` · ${m.primary_role}` : ""}
                    {i < post.tagged_contributor_ids.length - 1 ? ", " : ""}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
        </>
      )}
      <div className={`flex items-center gap-4 mt-4 pt-3.5 border-t flex-wrap ${borderCls}`}>
        <button onClick={toggleLike} data-testid={`community-like-btn-${post.id}`} className={`inline-flex items-center gap-1.5 text-xs transition-colors ${liked ? "text-red-500" : actionCls}`}>
          <Heart className={`h-3.5 w-3.5 ${liked ? "fill-current" : ""}`} /> {likeCount}
        </button>
        <button onClick={() => loadComments()} data-testid={`community-comment-toggle-${post.id}`} className={`inline-flex items-center gap-1.5 text-xs transition-colors ${actionCls}`}>
          <MessageCircle className="h-3.5 w-3.5" /> {commentCount}
        </button>
        <button onClick={report} disabled={reported} data-testid={`community-report-btn-${post.id}`} className={`inline-flex items-center gap-1.5 text-xs transition-colors disabled:opacity-40 ${actionCls}`}>
          <Flag className="h-3.5 w-3.5" /> {reported ? "Reported" : "Report"}
        </button>
        {isModerator && (
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <button onClick={() => moderate("pin", { pinned: !post.pinned })} disabled={busy} data-testid={`community-pin-btn-${post.id}`} className={`text-[11px] transition-colors ${actionCls}`}>
              {post.pinned ? "Unpin" : "Pin"}
            </button>
            <button onClick={() => moderate("feature", { featured: !post.featured })} disabled={busy} data-testid={`community-feature-btn-${post.id}`} className={`text-[11px] transition-colors ${actionCls}`}>
              {post.featured ? "Unfeature" : "Feature"}
            </button>
            <button onClick={() => moderate("hide", { hidden: !post.hidden })} disabled={busy} data-testid={`community-hide-btn-${post.id}`} className={`inline-flex items-center gap-1 text-[11px] transition-colors ${actionCls}`}>
              {post.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {post.hidden ? "Unhide" : "Hide"}
            </button>
          </div>
        )}
        {post.post_type === "feedback_request" && canDeletePost && (
          post.feedback_status === "CLOSED" ? (
            <button onClick={() => moderate("reopen-feedback")} disabled={busy} data-testid={`community-reopen-feedback-btn-${post.id}`}
              className={`${isModerator ? "" : "ml-auto"} inline-flex items-center gap-1 text-[11px] transition-colors ${actionCls}`}>
              Reopen feedback
            </button>
          ) : (
            <ConfirmDialog
              testIdPrefix={`community-close-feedback-${post.id}`}
              title="Close this feedback request?"
              description="Comments will stop; the video and existing comments stay visible to everyone."
              confirmLabel="Close feedback"
              onConfirm={() => moderate("close-feedback")}
              trigger={
                <button disabled={busy} data-testid={`community-close-feedback-btn-${post.id}`} className={`${isModerator ? "" : "ml-auto"} inline-flex items-center gap-1 text-[11px] transition-colors ${actionCls}`}>
                  Close feedback
                </button>
              }
            />
          )
        )}
        {canDeletePost && (
          <ConfirmDialog
            testIdPrefix={`community-delete-post-${post.id}`}
            title="Delete this post?"
            description="This permanently removes the post, its comments, likes, and any attached images. This can't be undone."
            confirmLabel="Delete post"
            onConfirm={() => moderate("delete")}
            trigger={
              <button disabled={busy} data-testid={`community-delete-btn-${post.id}`}
                className={`${isModerator || (post.post_type === "feedback_request") ? "" : "ml-auto"} inline-flex items-center gap-1 text-[11px] text-red-400 hover:text-red-500 transition-colors`}>
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            }
          />
        )}
      </div>
      {showComments && (
        <div className="mt-3.5 space-y-2" data-testid={`community-comments-${post.id}`}>
          {(comments || []).map((c) => (
            <div key={c.id} className={`flex items-center justify-between gap-2 text-xs ${dark ? "text-paper/70" : "text-ink-soft"}`}>
              <span>
                <MemberLink freelancerId={c.author_freelancer_id} className={`font-medium hover:underline ${textCls}`} testId={`community-comment-author-link-${c.id}`}>
                  {c.author_name}
                </MemberLink>
                : {renderMentions(c.text, members)}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {/* Code-review fix (2026-10, Cursor audit P1-2): c.author_id is now stripped
                    for a freelancer viewer — these two now read the server-computed
                    is_mine/can_delete flags instead of comparing a field that may be absent. */}
                {!c.is_mine && (
                  <button
                    onClick={() => reportComment(c.id)}
                    disabled={reportedComments.has(c.id)}
                    data-testid={`community-comment-report-${c.id}`}
                    className={`hover:text-red-400 disabled:opacity-40 ${softCls}`}
                  >
                    <Flag className="h-3 w-3" />
                  </button>
                )}
                {c.can_delete && (
                  <ConfirmDialog
                    testIdPrefix={`community-comment-remove-${c.id}`}
                    title="Delete this comment?"
                    description="This permanently removes the comment. This can't be undone."
                    confirmLabel="Delete comment"
                    onConfirm={() => removeComment(c.id)}
                    trigger={
                      <button data-testid={`community-comment-remove-${c.id}`} className={`shrink-0 hover:text-red-400 ${softCls}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          ))}
          {post.post_type === "feedback_request" && post.can_comment === false ? (
            <div className="text-xs text-faint italic py-2" data-testid={`community-feedback-comment-blocked-${post.id}`}>
              {post.comment_block_reason === "closed" ? "Feedback closed" : `Feedback is open to ${joinRolesNaturally(post.allowed_roles || [])}.`}
            </div>
          ) : (
            <>
              {post.post_type === "feedback_request" && (
                <div className="text-[11px] text-faint -mt-1 mb-1" data-testid={`community-feedback-helper-${post.id}`}>Share specific, constructive feedback</div>
              )}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    ref={commentInputRef}
                    value={commentText} onChange={handleCommentChange} onKeyDown={handleCommentKeyDown}
                    onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
                    placeholder="Write a comment… (@ to mention someone)"
                    data-testid={`community-comment-input-${post.id}`}
                    className={`w-full rounded-md px-2.5 py-1.5 text-xs placeholder:text-faint focus:outline-none focus:border-brand/50 ${dark ? "border border-white/15 bg-white/5 text-paper" : "border border-hairline bg-surface2/30 text-ink"}`}
                  />
                  {mentionOpen && mentionMatches.length > 0 && (
                    <div data-testid={`community-mention-menu-${post.id}`}
                      className={`absolute bottom-full left-0 mb-1 w-56 max-h-48 overflow-y-auto rounded-md border shadow-lg z-10 ${dark ? "bg-ink border-white/15" : "bg-paper border-hairline"}`}>
                      {mentionMatches.map((m, i) => (
                        <button key={m.id} type="button" onMouseDown={(e) => { e.preventDefault(); selectMention(m); }}
                          data-testid={`community-mention-option-${post.id}-${m.id}`}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors ${
                            i === mentionIndex ? (dark ? "bg-white/10" : "bg-brand/10") : ""
                          } ${dark ? "text-paper hover:bg-white/10" : "text-ink hover:bg-brand/5"}`}>
                          <span className="font-medium">{m.name}</span>
                          {m.primary_role && <span className={`text-[10px] ${softCls}`}>{m.primary_role}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={submitComment} disabled={posting} data-testid={`community-comment-submit-${post.id}`} className="text-brand disabled:opacity-50">
                  {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
